"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
  DownloadIcon as FiDownload,
  FileSearchIcon as FiSearch,
  FilterIcon as FiFilter,
  Trash2Icon as FiTrash2,
  RefreshCwIcon as FiRefreshCw,
  ChevronLeftIcon as FiChevronLeft,
  ChevronRightIcon as FiChevronRight,
  CheckIcon as FiCheck,
  EyeIcon as FiEye,
  XIcon as FiX,
  Cloud,
  FileText,
  Building2,
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Database,
  CreditCard,
  AlertCircle,
  ChevronDown,
} from "lucide-react"

const CloudCreditsTable = () => {
  const [credits, setCredits] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCredit, setSelectedCredit] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null)
  const recordsPerPage = 20

  // Check if user is admin on component mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const userInfo = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`)
        setIsAdmin(userInfo.data.isAdmin || false)
      } catch (error) {
        console.error("Error checking admin status:", error)
      }
    }
    checkAdminStatus()
  }, [])

  // Fetch cloud credit applications
  useEffect(() => {
    const fetchData = async () => {
      try {
        let response
        if (isAdmin) {
          response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/applications`, {
            params: {
              page: currentPage,
              limit: recordsPerPage
            }
          })
          setCredits(response.data.applications)
        } else {
          response = await axios.get(`${process.env.REACT_APP_API_URL}/api/applications`)
          setCredits(response.data)
        }
      } catch (error) {
        console.error("Error fetching cloud credits:", error)
      }
    }
    fetchData()
  }, [isAdmin, currentPage])

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/applications/${selectedDocId}`)
      setDeleteDialogOpen(false)
      setCredits((prev) => prev.filter((c) => c._id !== selectedDocId))
    } catch (error) {
      console.error("Error deleting cloud credit application:", error)
    }
  }

  const handleStatusToggle = (docId, currentStatus) => {
    setSelectedDocId(docId);
    setSelectedStatus(currentStatus);
    setStatusDialogOpen(true);
  }

  const handleQuickStatusChange = async (docId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/applications/${docId}/status`,
        { status: newStatus }
      );
      setCredits((prev) =>
        prev.map((c) => (c._id === docId ? { ...c, status: newStatus } : c))
      );
      setOpenStatusDropdown(null);
    } catch (error) {
      console.error("Error changing status:", error);
    }
  };

  const handleDownloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(credits)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "CloudCredits")
    XLSX.writeFile(wb, "Cloud_Credits_Applications.xlsx")
  }

  const handleViewDetails = (credit) => {
    setSelectedCredit(credit)
    setDetailsDialogOpen(true)
  }

  // Search & filter
  const filtered = credits.filter((item) => {
    const term = searchTerm.toLowerCase()
    if (filter) return item[filter]?.toString().toLowerCase().includes(term)
    return Object.values(item).some((v) => v?.toString().toLowerCase().includes(term))
  })

  const totalPages = isAdmin ? Math.ceil(filtered.length / recordsPerPage) : 1
  const displayed = isAdmin
    ? filtered.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)
    : filtered

 const getStatusChip = (status, docId) => {
  const baseChip = () => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <FiCheck className="w-3 h-3 mr-1" /> Approved
          </span>
        )
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <AlertCircle className="w-3 h-3 mr-1" /> Pending
          </span>
        )
      case "rejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <FiX className="w-3 h-3 mr-1" /> Rejected
          </span>
        )
      default:
        return <span className="text-gray-500 text-sm">Unknown</span>
    }
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="inline-flex items-center"
        onClick={(e) => {
          e.stopPropagation();
          setOpenStatusDropdown(openStatusDropdown === docId ? null : docId);
        }}
      >
        {baseChip()}
        <ChevronDown className="w-4 h-4 ml-1 text-gray-500" />
      </button>
      
      {openStatusDropdown === docId && (
        <div className="absolute z-10 mt-1 w-32 bg-white shadow-lg rounded-md py-1 border border-gray-200">
          {["pending", "approved", "rejected"].map((option) => (
            <button
              key={option}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center ${
                status === option ? "bg-gray-100 font-medium" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickStatusChange(docId, option);
              }}
            >
              {option === "approved" && <FiCheck className="w-3 h-3 mr-2 text-green-600" />}
              {option === "pending" && <AlertCircle className="w-3 h-3 mr-2 text-yellow-600" />}
              {option === "rejected" && <FiX className="w-3 h-3 mr-2 text-red-600" />}
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Background Pattern */}
      <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
        >
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Cloud className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Cloud Credits Applications
                </h1>
                <p className="text-gray-600 text-lg">
                  {isAdmin ? "Manage all applications" : "View your applications"}
                </p>
              </div>
            </div>
            {isAdmin && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all duration-300 text-lg"
              >
                <FiDownload className="text-xl" />
                Export to Excel
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <FiFilter className="text-purple-600 text-xl" />
            <h2 className="text-xl font-semibold text-gray-800">Search & Filter</h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-4 w-full">
            <div className="flex-1 space-y-2">
              <label htmlFor="filter-by" className="text-sm font-semibold text-gray-700">
                Filter By
              </label>
              <select
                id="filter-by"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              >
                <option value="">All</option>
                <option value="organisationLegalName">Organization Name</option>
                <option value="spocName">SPOC Name</option>
                <option value="spocEmail">SPOC Email</option>
                <option value="spocPhone">SPOC Phone</option>
                <option value="cloudProvider">Cloud Provider</option>
                <option value="status">Status</option>
              </select>
            </div>
            <div className="flex-1 space-y-2">
              <label htmlFor="search-term" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FiSearch className="text-base" />
                Search Term
              </label>
              <input
                id="search-term"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search applications..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
              />
            </div>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8 overflow-x-auto"
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Date",
                  "Organization",
                  "SPOC Name",
                  "SPOC Email",
                  "SPOC Phone",
                  "Cloud Provider",
                  "DPITT Cert",
                  "Funding",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-500 text-lg">
                    No applications found. Try adjusting your search or filter criteria.
                  </td>
                </tr>
              ) : (
                displayed.map((row) => {
                  const dt = new Date(row.createdAt)
                  const date = dt.toLocaleDateString()
                  return (
                    <tr key={row._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{date}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {row.organisationLegalName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.spocName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.spocEmail}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{row.spocPhone}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                        {row.cloudProvider || "Not specified"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.dpittCertificate ? (
                          <a
                            href={row.dpittCertificate}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                          >
                            <FileText className="w-4 h-4 mr-1" /> View
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                        {row.receivedFunding ? `$${row.fundingAmount || 'N/A'}` : 'No'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusChip(row.status, row._id)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-200 transition-colors"
                            onClick={() => handleViewDetails(row)}
                          >
                            <FiEye className="w-4 h-4 mr-1" /> Details
                          </button>
                          {isAdmin && (
                            <button
                              className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                              onClick={() => {
                                setSelectedDocId(row._id)
                                setDeleteDialogOpen(true)
                              }}
                            >
                              <FiTrash2 className="w-4 h-4 mr-1" />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </motion.div>

        {/* Pagination - Only show for admin */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((cp) => cp - 1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
                }`}
            >
              <FiChevronLeft className="text-xl" />
              Previous
            </motion.button>
            <div className="flex items-center gap-2">
              <span className="text-base text-gray-600">
                Page <span className="font-semibold text-purple-600">{currentPage}</span> of{" "}
                <span className="font-semibold text-purple-600">{totalPages || 1}</span>
              </span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((cp) => cp + 1)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
                }`}
            >
              Next
              <FiChevronRight className="text-xl" />
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Delete Dialog */}
      <AnimatePresence>
        {deleteDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FiTrash2 className="text-red-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                    <p className="text-sm text-gray-600">This action cannot be undone</p>
                  </div>
                </div>
                <p className="text-gray-700 text-base mb-6">Are you sure you want to delete this application?</p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDeleteDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleDeleteConfirm}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 text-base"
                  >
                    Yes, Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Toggle Dialog */}
      <AnimatePresence>
        {statusDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="text-purple-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Update Application Status</h3>
                    <p className="text-sm text-gray-600">Select the new status for this application</p>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    id="status-select"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStatusDialogOpen(false)}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleStatusConfirm}
                    className={`flex-1 px-4 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-base ${selectedStatus === "approved"
                        ? "bg-gradient-to-r from-green-600 to-emerald-600"
                        : selectedStatus === "rejected"
                          ? "bg-gradient-to-r from-red-600 to-pink-600"
                          : "bg-gradient-to-r from-yellow-600 to-amber-600"
                      }`}
                  >
                    <FiCheck className="text-lg" />
                    Update Status
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Application Details Dialog */}
      <AnimatePresence>
        {detailsDialogOpen && selectedCredit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    Application Details
                  </h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setDetailsDialogOpen(false)}
                    className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                  >
                    <FiX className="text-gray-600 hover:text-red-600" />
                  </motion.button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Organization Details */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Building2 className="text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-800">Organization Details</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Legal Name</p>
                        <p className="text-gray-800">{selectedCredit.organisationLegalName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Registered Address</p>
                        <p className="text-gray-800">{selectedCredit.registeredAddress}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Organization Age</p>
                        <p className="text-gray-800">{selectedCredit.organisationAge} years</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">DPITT Certificate</p>
                        {selectedCredit.dpittCertificate ? (
                          <a
                            href={selectedCredit.dpittCertificate}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-600 hover:text-blue-800"
                          >
                            <FileText className="w-4 h-4 mr-1" /> View Certificate
                          </a>
                        ) : (
                          <p className="text-gray-500">Not provided</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* SPOC Details */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <User className="text-purple-600" />
                      <h3 className="text-lg font-semibold text-gray-800">SPOC Details</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="text-gray-800">{selectedCredit.spocName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-gray-800">{selectedCredit.spocEmail}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="text-gray-800">{selectedCredit.spocPhone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Cloud & Funding Details */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Database className="text-green-600" />
                      <h3 className="text-lg font-semibold text-gray-800">Cloud & Funding</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Cloud Provider</p>
                        <p className="text-gray-800">{selectedCredit.cloudProvider || "Not specified"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Has Availed Credits Before</p>
                        <p className="text-gray-800">
                          {selectedCredit.hasAvailedCredits ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Currently Using Credits</p>
                        <p className="text-gray-800">
                          {selectedCredit.currentlyOnCredits ? "Yes" : "No"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Credits Remaining</p>
                        <p className="text-gray-800">
                          {selectedCredit.creditsRemaining || "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Funding & Status */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="text-amber-600" />
                      <h3 className="text-lg font-semibold text-gray-800">Funding & Status</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Received Funding</p>
                        <p className="text-gray-800">
                          {selectedCredit.receivedFunding ? `$${selectedCredit.fundingAmount || 'N/A'}` : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Application Status</p>
                        <div className="mt-1">{getStatusChip(selectedCredit.status, selectedCredit._id)}</div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Submitted On</p>
                        <p className="text-gray-800">
                          {new Date(selectedCredit.createdAt).toLocaleDateString()} at{" "}
                          {new Date(selectedCredit.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="text-gray-800">
                          {new Date(selectedCredit.updatedAt).toLocaleDateString()} at{" "}
                          {new Date(selectedCredit.updatedAt).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-gray-100 flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setDetailsDialogOpen(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CloudCreditsTable